/**
 * Error handling utilities for API responses.
 *
 * Works with backend error format:
 * { detail: { code: "ERROR_CODE", message: "...", error_id: "...", timestamp: "...",
 *             field?: "...", details?: {...} } }
 *
 * Usage:
 *   import { getErrorMessage, getFieldErrors, getErrorId } from '@/shared/utils';
 *
 *   try {
 *     await mutation.mutateAsync(data);
 *   } catch (error) {
 *     const message = getErrorMessage(error);  // Translated!
 *     const fieldErrors = getFieldErrors(error);  // For form validation
 *     const errorId = getErrorId(error);  // For user to report
 *   }
 */

import { AxiosError, isAxiosError } from 'axios';
import i18n from '../../i18n/config';

export interface ApiErrorDetail {
  code: string;
  message: string;
  error_id?: string;
  /** Server-stamped time the error was handled (ISO-8601 UTC, second precision). */
  timestamp?: string;
  field?: string;
  // Details can be either field validation errors or interpolation values for i18n
  details?: Record<string, { code: string; message: string }> | Record<string, string>;
}

export interface ApiError {
  response?: {
    data?: {
      detail?: ApiErrorDetail | string;
    };
    status?: number;
  };
}

/**
 * Extract error detail from an API error response.
 */
export function getErrorDetail(error: unknown): ApiErrorDetail | null {
  const axiosError = error as AxiosError<{ detail?: ApiErrorDetail | string }>;
  const detail = axiosError?.response?.data?.detail;

  if (!detail) return null;

  // Handle string detail (legacy format)
  if (typeof detail === 'string') {
    return { code: 'UNKNOWN_ERROR', message: detail };
  }

  return detail;
}

/**
 * Get a translated error message from an API error.
 *
 * Uses the error code as an i18n key in the 'errors' namespace.
 * Falls back to the English message from the backend if no translation exists.
 *
 * @param error - The error from an API call
 * @param interpolation - Optional interpolation values for the translation
 * @returns Translated error message
 */
export function getErrorMessage(
  error: unknown,
  interpolation?: Record<string, string | number>
): string {
  const detail = getErrorDetail(error);
  const localTimestamp = formatTimestamp();

  if (!detail) {
    // Fallback for non-API errors - include diagnostic info
    const axiosError = error as AxiosError;

    // Connectivity failure: axios ERR_NETWORK / "Network Error" / a TypeError
    // from a failed fetch() ("Failed to fetch", incl. CORS).
    if (isNetworkError(error)) {
      return i18n.t('errors:NETWORK_ERROR', {
        timestamp: localTimestamp,
        errorId: '',
        defaultValue: 'Network error. Check your connection and try again.',
      });
    }

    // Timeout
    if (axiosError?.code === 'ECONNABORTED') {
      return i18n.t('errors:TIMEOUT', {
        timestamp: localTimestamp,
        errorId: '',
        defaultValue: 'Request timed out. Please try again.',
      });
    }

    // A genuine non-Axios Error carrying its own message (e.g. a client-side
    // validation throw) surfaces that message. An AxiosError is deliberately
    // excluded: with no `{ detail }` envelope (a proxy/gateway 5xx with an empty
    // body, or any un-enveloped HTTP failure) its `.message` is the bare,
    // untranslated "Request failed with status code 500" — the error-handling
    // contract forbids surfacing that, so it falls through to the standard
    // SERVICE_UNAVAILABLE message + reference below.
    if (!isAxiosError(error) && error instanceof Error && error.message) {
      return error.message;
    }

    return i18n.t('errors:SERVICE_UNAVAILABLE', {
      timestamp: localTimestamp,
      errorId: '',
      defaultValue: 'Service temporarily unavailable. Please try again.',
    });
  }

  // Try to translate the error code
  // Use details from the API response for interpolation if available
  const translationKey = `errors:${detail.code}`;
  const translated = i18n.t(translationKey, {
    // The server stamps the moment it handled the error; only fall back to the
    // browser clock when the response predates that field.
    timestamp: detail.timestamp ?? localTimestamp,
    errorId: detail.error_id ?? '',
    ...detail.details,                 // Backend-provided interpolation values
    ...interpolation,                  // Caller overrides
    defaultValue: detail.message,
  });

  return translated;
}

/**
 * Translate a bare `(code, message)` pair the same way `getErrorMessage`
 * translates an API error envelope.
 *
 * For error channels that are not HTTP responses — the chat turn runner
 * publishes `{type: 'error', code, message}` over the observer WebSocket — the
 * contract is identical: translate by code, and fall back to the backend's
 * English message (which is written for the log) when there is no translation.
 */
export function translateErrorCode(
  code: string | undefined,
  message: string,
  params?: Record<string, unknown>,
): string {
  if (!code) return message;
  return i18n.t(`errors:${code}`, { defaultValue: message, ...params });
}

/**
 * Format the browser's current UTC time the same way the backend stamps errors
 * (ISO-8601, second precision). Only for failures that never reached the server.
 */
function formatTimestamp(): string {
  return `${new Date().toISOString().slice(0, 19)}Z`;
}

/**
 * Returns a unified "generic / unexpected" error message including a reference
 * (error ID + timestamp) so the user can quote it to support. Use this anywhere
 * we previously showed `common:errors.unexpected` or a hand-rolled "something
 * went wrong" string.
 *
 * - If `error` is an API error, the server's `error_id` + `timestamp` are shown —
 *   that pair is what locates the log line.
 * - Otherwise (network failure, render crash) there is no server time to report,
 *   so the browser clock stands in and no ID is available.
 */
export function getGenericErrorMessage(error?: unknown): string {
  const detail = error !== undefined ? getErrorDetail(error) : null;
  const timestamp = detail?.timestamp ?? formatTimestamp();
  const errorId = detail?.error_id ?? '';

  return i18n.t('errors:UNEXPECTED_ERROR', {
    timestamp,
    errorId,
    defaultValue:
      'An unexpected error occurred. Please try again. If this recurs, please report it using the support widget at the bottom corner. Reference: {{errorId}} at {{timestamp}}.',
  });
}

/**
 * Get field-level errors from a validation error response.
 * Handles both multi-field validation errors (details object) and single-field errors (field property).
 *
 * @param error - The error from an API call
 * @returns Record of field names to translated error messages, or null if not a field error
 */
export function getFieldErrors(
  error: unknown
): Record<string, string> | null {
  const detail = getErrorDetail(error);

  if (!detail) return null;

  const fieldErrors: Record<string, string> = {};

  // Handle multi-field validation errors (details object)
  if (detail.details) {
    for (const [field, fieldError] of Object.entries(detail.details)) {
      const translationKey = `errors:${fieldError.code}`;
      fieldErrors[field] = i18n.t(translationKey, {
        field,
        defaultValue: fieldError.message,
      });
    }
  }

  // Handle single-field errors (field property on main error)
  if (detail.field && !fieldErrors[detail.field]) {
    const translationKey = `errors:${detail.code}`;
    fieldErrors[detail.field] = i18n.t(translationKey, {
      field: detail.field,
      defaultValue: detail.message,
    });
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}

/**
 * Check if an error has a specific error code.
 */
export function hasErrorCode(error: unknown, code: string): boolean {
  const detail = getErrorDetail(error);
  return detail?.code === code;
}

/**
 * Get the HTTP status code from an error.
 */
export function getErrorStatus(error: unknown): number | null {
  const axiosError = error as AxiosError;
  return axiosError?.response?.status ?? null;
}

/**
 * Check if an error is a connectivity / network failure rather than an HTTP
 * error with a response. True for axios `ERR_NETWORK` (request made, no reply —
 * the offline signature), a `"Network Error"` message, or a `TypeError` from a
 * failed `fetch()` ("Failed to fetch"). These are exactly the failures a device
 * produces when it has no network — the cold-start Airplane-Mode case.
 *
 * Used to keep background read-query toasts calm: a connectivity failure means
 * "you're offline", which the persistent OfflineBanner already communicates, so
 * the per-query "Failed to load" toast is suppressed even before the async
 * offline signal (onlineManager / OfflineStatusContext) has propagated.
 */
export function isNetworkError(error: unknown): boolean {
  const axiosError = error as AxiosError;
  if (axiosError?.code === 'ERR_NETWORK' || axiosError?.message === 'Network Error') {
    return true;
  }
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  return false;
}

/**
 * Check if error is a 401 Unauthorized.
 */
export function isUnauthorizedError(error: unknown): boolean {
  return getErrorStatus(error) === 401;
}

/**
 * Check if error is a 403 Forbidden.
 */
export function isForbiddenError(error: unknown): boolean {
  return getErrorStatus(error) === 403;
}

/**
 * Check if error is a 404 Not Found.
 */
export function isNotFoundError(error: unknown): boolean {
  return getErrorStatus(error) === 404;
}

/**
 * Check if error is a validation error (has field-level details).
 */
export function isValidationError(error: unknown): boolean {
  const detail = getErrorDetail(error);
  return detail?.code === 'VALIDATION_ERROR' && !!detail.details;
}

/**
 * Get the error ID from an API error for debugging/reporting.
 *
 * The error_id can be used to correlate frontend errors with backend logs.
 * Users can report this ID when contacting support.
 */
export function getErrorId(error: unknown): string | null {
  const detail = getErrorDetail(error);
  return detail?.error_id ?? null;
}

/**
 * Get the error code from an API error.
 */
export function getErrorCode(error: unknown): string | null {
  const detail = getErrorDetail(error);
  return detail?.code ?? null;
}
